import { authSession } from '@/shared/api'
import { env } from '@/shared/config'
import { fetchRealtimeCredentials, realtime } from '@/shared/realtime'

import { ensureAuthReady, onSignedIn, refreshCredential, signOut } from '@/features/auth'

/**
 * ─── SEAM (filled) ───────────────────────────────────────────────────────────
 * Wires the two things `shared/` deliberately does not know:
 *
 *   refresh        — how to obtain a new credential after a 401
 *   onSessionLost  — what happens when that fails
 *
 * The 401 machinery itself (one refresh at a time, one replay per request, no
 * loops) already lives in `shared/api` and needs no changes.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function wireAuth({ onSessionLost }: { onSessionLost: () => void }) {
  authSession.configure({
    refresh: refreshCredential,

    onSessionLost: () => {
      // Sign-out ends the provider session and runs every reset: query cache,
      // every store, the stream connection, both tokens.
      void signOut().finally(onSessionLost)
    },
  })

  if (env.VITE_REALTIME_URL !== undefined) {
    // The connection lives above the router, so navigation never drops it. Its
    // token endpoint is authenticated, so it follows the session: it opens
    // once a credential exists, and `runResets()` closes it on sign-out.
    //
    // A credentials getter, never a captured value — stream tokens live 60
    // seconds and every reconnect must fetch a fresh one.
    onSignedIn(() => realtime.connect(fetchRealtimeCredentials))
  }

  // Exchanges a stored refresh token for an access token before the first
  // render, so the guard's own `await` is usually already settled. Subscribed
  // to above first, so a credential restored here is not missed.
  void ensureAuthReady()
}
